import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { nomCategorie } from '../../analysis/donnees'
import { coutAnnuel, deriverEcheances, prochaineEcheance, type Echeance } from '../../analysis/recurrences'
import {
  enregistrerRecurrence,
  ignorerEcheance,
  realiserEcheance,
  retablirEcheance,
  supprimerRecurrence,
} from '../../db/repo'
import { formatMontant } from '../../domain/argent'
import { aujourdhui, dernierJourDuMois, formatJour, formatJourCourt, moisCourant, premierJourDuMois } from '../../domain/temps'
import { PERIODICITES, PERIODICITE_LABELS, type Cents, type Id, type Periodicite, type Sens } from '../../domain/types'
import {
  Bascule,
  Carte,
  Champ,
  ChampMontant,
  Confirmation,
  Encart,
  Entete,
  IconeChevron,
  IconePlus,
  Montant,
  NavMois,
  Puce,
  SelectCategorie,
  SelectCompte,
  useAction,
} from '../composants'
import { useDonnees } from '../donnees'

export function PageRecurrences() {
  const { d, ix } = useDonnees()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const mois = params.get('mois') ?? moisCourant()
  const jour = aujourdhui()

  const echeances = useMemo(
    () => deriverEcheances(d.recurrences, d.exceptions, d.operations, premierJourDuMois(mois), dernierJourDuMois(mois), jour),
    [d, mois, jour],
  )

  const regles = [...d.recurrences].sort((a, b) => b.actif - a.actif || a.libelle.localeCompare(b.libelle, 'fr'))

  return (
    <>
      <Entete titre="Récurrences" sous="Échéancier et règles" retour />
      <main className="contenu">
        <NavMois mois={mois} onChange={(m) => setParams({ mois: m }, { replace: true })} />

        <Carte titre="Échéancier">
          {echeances.length === 0 ? (
            <p className="tres-doux">Aucune échéance ce mois-ci.</p>
          ) : (
            <ul className="liste">
              {echeances.map((e) => (
                <LigneEcheance key={`${e.recurrence.id}${e.occurrence}`} e={e} />
              ))}
            </ul>
          )}
        </Carte>

        <Carte titre="Règles">
          {regles.length === 0 ? (
            <p className="tres-doux">
              Déclarez ici ce qui revient à intervalle régulier : primes LAMal, loyer, abonnements, salaire. Leurs échéances apparaissent
              d’avance dans le prévisionnel.
            </p>
          ) : (
            <ul className="liste">
              {regles.map((r) => {
                const prochaine = r.actif ? prochaineEcheance(r, jour) : null
                return (
                  <li key={r.id}>
                    <Link to={`/recurrences/${r.id}`} className="item">
                      <div className="grandir">
                        <div className="item__principal">{r.libelle}</div>
                        <div className="item__secondaire">
                          {PERIODICITE_LABELS[r.periodicite]}
                          {r.intervalle > 1 ? ` ×${r.intervalle}` : ''} · {nomCategorie(ix, r.categorieId)} · {ix.compteParId.get(r.compteId)?.nom}
                          {prochaine && ` · prochaine le ${formatJourCourt(prochaine)}`}
                        </div>
                        <div className="tres-doux">{formatMontant(coutAnnuel(r))} par an</div>
                      </div>
                      {!r.actif && <Puce>inactive</Puce>}
                      {r.modeleRepartitionId && <Puce ton="info">partagée</Puce>}
                      <Montant valeur={r.montant} sens={r.sens} />
                      <span className="chevron"><IconeChevron /></span>
                    </Link>
                  </li>
                )
              })}
            </ul>
          )}
        </Carte>
      </main>
      <button type="button" className="fab" aria-label="Nouvelle récurrence" onClick={() => navigate('/recurrences/nouvelle')}>
        <IconePlus />
      </button>
    </>
  )
}

function LigneEcheance({ e }: { e: Echeance }) {
  const { ix } = useDonnees()
  const [saisie, setSaisie] = useState<{ montant: Cents | null; date: string } | null>(null)
  const { erreur, executer } = useAction()
  const r = e.recurrence

  return (
    <li className="part">
      <div className="ligne ligne--espacee">
        <div className="grandir">
          <div className="gras">{r.libelle}</div>
          <div className="tres-doux">
            {formatJour(e.date)} · {ix.compteParId.get(e.operation?.compteId ?? r.compteId)?.nom}
          </div>
        </div>
        {e.statut === 'realisee' && <Puce ton="ok">saisie</Puce>}
        {e.statut === 'ignoree' && <Puce>ignorée</Puce>}
        {e.statut === 'prevue' && (e.enRetard ? <Puce ton="alerte">en retard</Puce> : <Puce ton="info">prévue</Puce>)}
        <Montant valeur={e.statut === 'ignoree' ? r.montant : e.montant} sens={r.sens} />
      </div>

      {e.statut === 'realisee' && e.operation && (
        <Link to={`/operations/${e.operation.id}`} className="btn btn--fantome btn--petit">Voir l’opération</Link>
      )}
      {e.statut === 'ignoree' && (
        <button type="button" className="btn btn--petit" onClick={() => void retablirEcheance(r.id, e.occurrence)}>Rétablir</button>
      )}
      {e.statut === 'prevue' &&
        (saisie ? (
          <div className="pile">
            <div className="grille-2">
              <Champ libelle="Montant réel">
                <ChampMontant valeur={saisie.montant} onChange={(m) => setSaisie({ ...saisie, montant: m })} />
              </Champ>
              <Champ libelle="Date réelle">
                <input type="date" value={saisie.date} onChange={(ev) => setSaisie({ ...saisie, date: ev.target.value })} />
              </Champ>
            </div>
            <div className="boutons">
              <button type="button" className="btn btn--petit" onClick={() => setSaisie(null)}>Annuler</button>
              <button
                type="button"
                className="btn btn--petit btn--principal"
                onClick={() =>
                  void executer(async () => {
                    if (!saisie.montant) throw new Error('Indiquez le montant.')
                    await realiserEcheance(r.id, e.occurrence, { montant: saisie.montant, date: saisie.date })
                    setSaisie(null)
                  })
                }
              >
                Valider
              </button>
            </div>
          </div>
        ) : (
          <div className="boutons">
            <button type="button" className="btn btn--petit btn--principal" onClick={() => setSaisie({ montant: r.montant, date: e.occurrence })}>
              Saisir
            </button>
            <button type="button" className="btn btn--petit" onClick={() => void ignorerEcheance(r.id, e.occurrence)}>
              Ignorer
            </button>
          </div>
        ))}
      {erreur && <Encart ton="danger">{erreur}</Encart>}
    </li>
  )
}

export function PageRecurrence() {
  const { id } = useParams()
  const { d, reglages } = useDonnees()
  const navigate = useNavigate()
  const existante = id && id !== 'nouvelle' ? d.recurrences.find((r) => r.id === id) : undefined

  const [sens, setSens] = useState<Sens>(existante?.sens ?? 'depense')
  const [libelle, setLibelle] = useState(existante?.libelle ?? '')
  const [montant, setMontant] = useState<Cents | null>(existante?.montant ?? null)
  const [periodicite, setPeriodicite] = useState<Periodicite>(existante?.periodicite ?? 'mensuel')
  const [intervalle, setIntervalle] = useState(existante?.intervalle ?? 1)
  const [debut, setDebut] = useState(existante?.debut ?? aujourdhui())
  const [fin, setFin] = useState(existante?.fin ?? '')
  const [compteId, setCompteId] = useState<Id | null>(existante?.compteId ?? reglages.compteParDefautId ?? d.comptes[0]?.id ?? null)
  const [categorieId, setCategorieId] = useState<Id | null>(existante?.categorieId ?? null)
  const [modeleId, setModeleId] = useState<Id | null>(existante?.modeleRepartitionId ?? null)
  const [note, setNote] = useState(existante?.note ?? '')
  const [actif, setActif] = useState<0 | 1>(existante?.actif ?? 1)
  const [confirmer, setConfirmer] = useState(false)
  const { erreur, executer } = useAction()

  if (id && id !== 'nouvelle' && !existante) {
    return (
      <>
        <Entete titre="Récurrence" retour />
        <main className="contenu"><p className="vide">Cette récurrence n’existe plus.</p></main>
      </>
    )
  }

  const enregistrer = () =>
    executer(async () => {
      if (!libelle.trim()) throw new Error('Donnez un libellé.')
      if (!montant) throw new Error('Indiquez un montant.')
      if (!compteId || !categorieId) throw new Error('Choisissez un compte et une catégorie.')
      if (fin && fin < debut) throw new Error('La fin précède le début.')
      await enregistrerRecurrence({
        id: existante?.id,
        sens,
        libelle: libelle.trim(),
        montant,
        periodicite,
        intervalle,
        debut,
        fin: fin || null,
        compteId,
        categorieId,
        modeleRepartitionId: sens === 'depense' ? modeleId : null,
        note,
        actif,
      })
      navigate(-1)
    })

  return (
    <>
      <Entete titre={existante ? 'Récurrence' : 'Nouvelle récurrence'} retour />
      <main className="contenu">
        <Carte>
          <div className="pile">
            <Bascule etiquette="Sens" valeur={sens} onChange={setSens} options={[{ valeur: 'depense', libelle: 'Dépense' }, { valeur: 'revenu', libelle: 'Revenu' }]} />
            <Champ libelle="Libellé">
              <input type="text" value={libelle} placeholder="Prime LAMal, loyer, salaire…" onChange={(e) => setLibelle(e.target.value)} />
            </Champ>
            <Champ libelle="Montant (CHF)">
              <ChampMontant valeur={montant} onChange={setMontant} grand />
            </Champ>
            <div className="grille-2">
              <Champ libelle="Fréquence">
                <select value={periodicite} onChange={(e) => setPeriodicite(e.target.value as Periodicite)}>
                  {PERIODICITES.map((p) => (
                    <option key={p} value={p}>{PERIODICITE_LABELS[p]}</option>
                  ))}
                </select>
              </Champ>
              <Champ libelle="Répéter tous les" aide="1 = à chaque période">
                <input type="number" min={1} max={120} value={intervalle} onChange={(e) => setIntervalle(Math.max(1, Number(e.target.value) || 1))} />
              </Champ>
            </div>
            <div className="grille-2">
              <Champ libelle="Première échéance" aide="Son jour du mois est conservé, même après un mois court.">
                <input type="date" value={debut} onChange={(e) => setDebut(e.target.value)} />
              </Champ>
              <Champ libelle="Dernière (facultatif)">
                <input type="date" value={fin} onChange={(e) => setFin(e.target.value)} />
              </Champ>
            </div>
            <Champ libelle="Compte débité">
              <SelectCompte comptes={d.comptes} valeur={compteId} onChange={setCompteId} />
            </Champ>
            <Champ libelle="Catégorie">
              <SelectCategorie categories={d.categories} sens={sens} valeur={categorieId} onChange={setCategorieId} />
            </Champ>
            {sens === 'depense' && (
              <Champ libelle="Répartition" aide="Appliquée à chaque échéance saisie. Sans modèle, la dépense est entièrement à votre charge.">
                <select value={modeleId ?? ''} onChange={(e) => setModeleId(e.target.value || null)}>
                  <option value="">Aucune (à ma charge)</option>
                  {d.modeles.map((m) => (
                    <option key={m.id} value={m.id}>{m.nom}</option>
                  ))}
                </select>
              </Champ>
            )}
            <Champ libelle="Note">
              <textarea value={note} rows={2} onChange={(e) => setNote(e.target.value)} />
            </Champ>
            <label className="case">
              <input type="checkbox" checked={actif === 1} onChange={(e) => setActif(e.target.checked ? 1 : 0)} />
              Active (décocher pour un abonnement résilié : l’historique reste)
            </label>
          </div>
        </Carte>
        {erreur && <Encart ton="danger">{erreur}</Encart>}
        <button type="button" className="btn btn--principal btn--bloc" onClick={() => void enregistrer()}>Enregistrer</button>
        {existante && (
          <button type="button" className="btn btn--danger btn--bloc" onClick={() => setConfirmer(true)}>Supprimer la règle</button>
        )}
      </main>
      {confirmer && existante && (
        <Confirmation
          titre="Supprimer la récurrence ?"
          message="Les opérations déjà saisies sont conservées ; seules les échéances à venir disparaissent. Pour un abonnement résilié, préférez la désactiver."
          libelleConfirmer="Supprimer"
          danger
          onAnnuler={() => setConfirmer(false)}
          onConfirmer={() => void supprimerRecurrence(existante.id).then(() => navigate('/recurrences', { replace: true }))}
        />
      )}
    </>
  )
}
